/* eslint-disable no-unused-expressions */
// Copyright (c) 2022 8th Wall, Inc.
//
// app.js is the main entry point for your 8th Wall app. Code here will execute after head.html
// is loaded, and before body.html is loaded.
/* globals XR8 XRExtras THREE TWEEN AFRAME */

import { gsap } from 'gsap';
import { MotionPathPlugin } from 'gsap/MotionPathPlugin.js';
import './index.scss';
import './fonts-stylesheet.css';
import Config from './src/js/config.js';
// import './src/js/aframe-particle-system-component/aframe-particle-system-component.js';

import {
  gltfMorphComponent,
  ignoreRaycast,
  testerComponent,
} from './src/js/components.js';
import { cubeMapRealtimeComponent } from './src/js/cubemap-realtime.js';
import { cubeEnvMapComponent } from './src/js/cubemap-static.js';
import { responsiveImmersiveComponent } from './src/js/responsive-immersive.js';
import { xrLightComponent, xrLightSystem } from './src/js/xrlight.js';
import { splashComponent } from './src/js/splash.js';
import { cameraHudComponent } from './src/js/camera-hud.js';
import { gameManagerComponent } from './src/js/game-manager.js';
import { audioManagerComponent } from './src/js/audio-manager.js';
import { inputManagerComponent } from './src/js/input-manager.js';
import { particleManagerComponent } from './src/js/particle-manager.js';
import { lookAtComponent } from './src/js/look-at-component.js';
import { textFontComponent } from './src/js/font-components.js';
import { drawOnTop } from './src/js/draw-on-top.js';
import { tapPlaceCursorComponent } from './src/js/tap-place-cursor.js';
import { enemyMonsterComponent } from './src/js/enemy-monster.js';
import { enemyProjectileComponent } from './src/js/enemy-projectile.js';
import { laserComponent } from './src/js/laser.js';
import { damageBadgeComponent } from './src/js/damage-badge.js';
import { cameraShaderComponent } from './src/js/camera-shader.js';

import downloadImg from './assets/ui-images/download-v1.svg';

const registerCustomComponent = (componentName = '', componentScript) => {
  AFRAME.registerComponent(componentName, componentScript);
  Config.customComponentsNames.push(componentName); // This is used to fix a weird A-FRAME bug where the components don't call init() on start
};

// registerCustomComponent('annotation', annotationComponent)
// registerCustomComponent('absolute-pinch-scale', absPinchScaleComponent)
// registerCustomComponent('proximity', proximityComponent)
registerCustomComponent('gltf-morph', gltfMorphComponent);
registerCustomComponent('ignore-raycast', ignoreRaycast);
registerCustomComponent('tester', testerComponent);
registerCustomComponent('cubemap-realtime', cubeMapRealtimeComponent);
registerCustomComponent('cubemap-static', cubeEnvMapComponent);
registerCustomComponent('responsive-immersive', responsiveImmersiveComponent);
registerCustomComponent('xr-light', xrLightComponent);
AFRAME.registerSystem('xr-light', xrLightSystem);
registerCustomComponent('splash', splashComponent);
registerCustomComponent('camera-hud', cameraHudComponent);
registerCustomComponent('game-manager', gameManagerComponent);
registerCustomComponent('audio-manager', audioManagerComponent);
registerCustomComponent('input-manager', inputManagerComponent);
registerCustomComponent('particle-manager', particleManagerComponent);
registerCustomComponent('look-at', lookAtComponent);
registerCustomComponent('text-font', textFontComponent());
registerCustomComponent('draw-on-top', drawOnTop);
registerCustomComponent('tap-place-cursor', tapPlaceCursorComponent);
registerCustomComponent('enemy-monster', enemyMonsterComponent);
registerCustomComponent('enemy-projectile', enemyProjectileComponent);
registerCustomComponent('laser', laserComponent);
registerCustomComponent('damage-badge', damageBadgeComponent);
registerCustomComponent('camera-shader', cameraShaderComponent);

gsap.registerPlugin(MotionPathPlugin);

// add 'Tap + Hold to Add to Photos' prompt when user takes a photo
window.addEventListener('mediarecorder-photocomplete', () => {
  document.getElementById('overlay')
    ? (document.getElementById('overlay').style.display = 'block')
    : document.body.insertAdjacentHTML(
      'beforeend',
      window.isDesktop
        ? `<div id="overlay"><div id="savePrompt">To save: Right Click on the scene OR use the <img src="${downloadImg}"> button below</div></div>`
        : `<div id="overlay"><div id="savePrompt">To save: Tap and hold on the scene OR use the <img src="${downloadImg}"> button below</div></div>`
    );
  showHideDesktopBackground(true);
});

// hide 'Tap + Hold to Add to Photos' prompt when user dismisses preview modal
window.addEventListener('mediarecorder-previewclosed', () => {
  if (document.getElementById('overlay')) {
    document.getElementById('overlay').style.display = 'none';
    document.getElementsByClassName('recorder-container')[0].style.display =
      'none';
  }
});

const onxrloaded = () => {
  // XR8.CanvasScreenshot.configure({ maxDimension: 1920, jpgCompression: 100 });
};

window.XR8 ? onxrloaded() : window.addEventListener('xrloaded', onxrloaded);

window.onload = () => {
  window.isAlreadyLoaded = true;
};

const aScene = document.querySelector('a-scene');
aScene.addEventListener('loaded', () => {
  setTimeout(() => {
    checkComponentsInit();
    window.splashComponentRef.customInit();
  }, 1);
});
const checkComponentsInit = () => { // This is used to fix a weird A-FRAME bug where the components don't call init() on start
  const allElements = aScene.querySelectorAll('*');
  const allElementsArray = [...allElements];
  allElementsArray.push(aScene);
  allElementsArray.forEach((element) => {
    const attributeNames = element.getAttributeNames();
    attributeNames.forEach((attributeName) => {
      if (Config.customComponentsNames.includes(attributeName)) {
        //console.log('>> attributeNames - attributeName before:', attributeName, '-', element.components[attributeName]);
        element.setAttribute(attributeName, '');
      }
    });
  });
};

// bitmaps cause texture issues on iOS this workaround prevents black textures and crashes
const IS_IOS = /^(iPad|iPhone|iPod)/.test(window.navigator.platform)
  || (/^Mac/.test(window.navigator.platform) && window.navigator.maxTouchPoints > 1);
if (IS_IOS) {
  window.createImageBitmap = undefined;
}
